// Generador de HTML de impresión del check list de equipos.
// Se usa junto con un iframe oculto (ver helpers/imprimirCheckList) para
// imprimir sin abrir una pestaña nueva del navegador.

const esc = (raw) => String(raw ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatChlId = (id) => `CHL-${String(id).padStart(9, '0')}`;

const formatFecha = (value) => {
  if (!value) return '___/___/______';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '___/___/______';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Nombre con el que el navegador propone guardar el PDF:
// "Check list Ingreso - VOL-01". Sin esto usa el título de la app, con el deploy.
export const tituloCheckList = ({ checklist, equipo, plantilla, blank }) => {
  const interno = equipo?.interno || checklist?.equipo_interno || '';
  const tipo = checklist?.tipo
    ? checklist.tipo.charAt(0).toUpperCase() + checklist.tipo.slice(1)
    : '';
  const base = blank || !tipo ? 'Check list en blanco' : `Check list ${tipo}`;
  const sufijo = interno || plantilla?.familia_nombre || '';
  return sufijo ? `${base} - ${sufijo}` : base;
};

const ESTADO_ABREV = {
  bueno: 'B', regular: 'R', malo: 'M',
  ver_observaciones: 'VO', no_controlado: 'NC',
  posee: 'P', no_posee: 'NP',
};

const ESTADO_COLOR_KEY = {
  malo: 'printEstadoBad', no_posee: 'printEstadoBad',
  regular: 'printEstadoRegular',
  bueno: 'printEstadoGood', posee: 'printEstadoGood',
  ver_observaciones: 'printEstadoWarn',
};

const agruparPorSistema = (items) =>
  items.reduce((acc, it) => {
    const k = it.sistema_nombre || 'Sin sistema';
    if (!acc[k]) acc[k] = [];
    acc[k].push(it);
    return acc;
  }, {});

const chunk = (arr, size) => {
  const res = [];
  for (let i = 0; i < arr.length; i += size) res.push(arr.slice(i, i + size));
  return res;
};

const sistemaHtml = (sistema, pts, blank, theme) => {
  const filas = chunk(pts, 3);
  const filasHtml = filas.map((fila) => {
    const celdas = [0, 1, 2].map((ci) => {
      const item = fila[ci];
      if (!item) return `<td></td><td class="estado"></td>`;
      const nombre = blank ? (item.catalogo_nombre || item.nombre) : item.punto_nombre;
      const estado = blank ? null : item.estado;
      const colorKey = ESTADO_COLOR_KEY[estado] || 'printEstadoNeutral';
      const abrev = estado ? (ESTADO_ABREV[estado] || estado) : '';
      return `<td>${esc(nombre)}</td><td class="estado" style="color:${theme.colors[colorKey]}">${esc(abrev)}</td>`;
    }).join('');

    const tieneObs = !blank && fila.some((it) => it?.observacion);
    const obsRow = tieneObs
      ? `<tr class="obs-row">${[0, 1, 2].map((ci) => {
          const item = fila[ci];
          return item?.observacion
            ? `<td colspan="2">${esc(item.observacion)}</td>`
            : `<td colspan="1" style="border:none;background:transparent"></td>`;
        }).join('')}</tr>`
      : '';

    return `<tr>${celdas}</tr>${obsRow}`;
  }).join('');

  return `
    <div class="sistema">
      <div class="section-title">${esc(sistema)}</div>
      <table class="check-table">
        <thead>
          <tr>
            <th style="width:28%">Componente</th><th style="width:5%">Est.</th>
            <th style="width:28%">Componente</th><th style="width:5%">Est.</th>
            <th style="width:28%">Componente</th><th style="width:5%">Est.</th>
          </tr>
        </thead>
        <tbody>${filasHtml}</tbody>
      </table>
    </div>
  `;
};

export const buildCheckListPrintHtml = ({ checklist, equipo, plantilla, blank, logoUrl, theme }) => {
  const items = blank ? (plantilla?.puntos_control || []) : (checklist?.items || []);
  const porSistema = agruparPorSistema(items);
  const sistemasHtml = Object.entries(porSistema)
    .map(([sist, pts]) => sistemaHtml(sist, pts, blank, theme))
    .join('');

  const familiaNombre = equipo?.familia_nombre || plantilla?.familia_nombre || '';
  const marcaModelo = [equipo?.marca_nombre || equipo?.equipo_marca, equipo?.modelo_nombre].filter(Boolean).join(' - ');
  const fechaFormato = blank ? '' : formatFecha(checklist?.fecha_creacion);

  const logoHtml = logoUrl ? `<img class="logo" src="${esc(logoUrl)}" alt="Logo" />` : '';

  const firmasHtml = ['recepcion', 'despacho'].map((rol) => {
    const firma = checklist?.firmas?.find((f) => f.rol === rol);
    const titulo = rol === 'recepcion' ? 'Firma y Aclaración de Recepción' : 'Firma y Aclaración de Despacho';
    let cuerpo;
    if (firma?.imagen && !blank) {
      cuerpo = `<img class="firma-img" src="${esc(firma.imagen)}" alt="Firma" />`;
      if (firma?.nombre_firmante) {
        cuerpo += `<div class="firma-nombre">${esc(firma.nombre_firmante)}</div>`;
      }
    } else {
      cuerpo = `<div class="firma-linea">Firma y aclaración</div>`;
    }
    return `
      <div class="sign-box">
        <div class="sign-label">${titulo}</div>
        ${cuerpo}
      </div>
    `;
  }).join('');

  const fotosHtml = (!blank && checklist?.fotos?.length > 0)
    ? `
      <div class="page-break">
        <div class="section-title" style="margin-top:8px">Fotos del equipo</div>
        <div class="foto-grid">
          ${checklist.fotos.map((f) => `<img class="foto-thumb" src="${esc(f.imagen)}" alt="" />`).join('')}
        </div>
      </div>
    `
    : '';

  const resultOpActivo = !blank && checklist?.resultado === 'operativo';
  const resultCondActivo = !blank && checklist?.resultado === 'condicional';

  const title = tituloCheckList({ checklist, equipo, plantilla, blank });

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${esc(title)}</title>
        <style>
          @page { size: A4 portrait; margin: 8mm 10mm; }
          * { box-sizing: border-box; }
          html, body {
            margin: 0; padding: 0;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10.5pt;
            color: ${theme.colors.printText};
            background: ${theme.colors.printBg};
          }
          /* 1mm de aire a la derecha: con border-collapse la mitad del borde
             exterior de las tablas cae fuera de su caja y el margen de la hoja
             la recortaba (se perdía la línea derecha de las tablas). */
          .wrap { padding: 0 1mm 0 0; width: 100%; }

          .doc-header {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;
            border-bottom: 2px solid ${theme.colors.printBorder};
            padding-bottom: 6px;
          }
          .doc-title { font-size: 12pt; font-weight: 900; letter-spacing: 0.05em; text-transform: uppercase; }
          .doc-subtitle { font-size: 10pt; font-weight: 700; margin-top: 2px; }
          .familia-header { font-size: 18pt; font-weight: 900; text-transform: uppercase; text-align: center; letter-spacing: 0.05em; }
          .doc-header-right { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
          .tipo-box { display: flex; flex-direction: column; gap: 2px; font-size: 8.5pt; text-align: right; }
          .logo { max-width: 90px; max-height: 60px; object-fit: contain; }

          .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 14px; margin-bottom: 6px; }
          .date-grid {
            display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 12px;
            border: 1px solid ${theme.colors.printBorderLight}; border-radius: 4px;
            padding: 5px 8px; margin-bottom: 4px;
          }
          .info-label { font-size: 7.5pt; font-weight: 700; color: ${theme.colors.printRowObsText}; text-transform: uppercase; letter-spacing: 0.03em; }
          .info-value { font-size: 9.5pt; }

          .section-title {
            background: ${theme.colors.printSectionBg}; font-weight: 700; font-size: 10.5pt;
            text-align: center; padding: 2px 4px; text-transform: uppercase; letter-spacing: 0.05em;
          }

          .sistema { margin-bottom: 3px; }
          .check-table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
          .check-table td, .check-table th {
            border: 1px solid ${theme.colors.printBorder}; padding: 2px 4px; font-size: 9.5pt;
            vertical-align: middle; background: ${theme.colors.printBg};
          }
          .check-table th { background: ${theme.colors.printTableHeaderBg}; font-weight: 700; text-align: center; font-size: 9pt; }
          .check-table td.estado { text-align: center; font-weight: 700; font-size: 9.5pt; min-width: 26px; background: ${theme.colors.printBg} !important; }
          .obs-row td { font-size: 8.5pt; font-style: italic; color: ${theme.colors.printRowObsText}; background: ${theme.colors.printRowObsBg}; }

          .referencia-table { width: 100%; border-collapse: collapse; margin-top: 4px; }
          .referencia-table td { border: 1px solid ${theme.colors.printBorder}; padding: 2px 4px; font-size: 9pt; }

          .obs-block { border: 1px solid ${theme.colors.printBorder}; min-height: 40px; padding: 3px 5px; font-size: 10pt; margin-top: 3px; }
          .lined-block {
            min-height: 60px; border: 1px solid ${theme.colors.printBorder}; padding: 3px 5px; font-size: 10pt;
            background: repeating-linear-gradient(to bottom, ${theme.colors.printBg}, ${theme.colors.printBg} 15px, ${theme.colors.printLinedBg} 15px, ${theme.colors.printLinedBg} 16px);
          }

          .result-row { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 6px; }
          .result-box { border: 2px solid ${theme.colors.printBorder}; padding: 4px 8px; text-align: center; font-weight: 700; font-size: 10.5pt; }
          .result-ok { background: ${resultOpActivo ? theme.colors.printResultadoOk : theme.colors.printBg}; }
          .result-cond { background: ${resultCondActivo ? theme.colors.printResultadoCond : theme.colors.printBg}; }
          .nota-condicional { font-size: 8.5pt; margin-top: 2px; border-top: 1px solid ${theme.colors.printBorder}; padding-top: 2px; }

          .sign-section { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 6px; }
          .sign-box { border: 1px solid ${theme.colors.printBorder}; min-height: 65px; padding: 4px; font-size: 10pt; position: relative; }
          .sign-label { font-weight: 700; font-size: 9.5pt; text-transform: uppercase; margin-bottom: 4px; border-bottom: 1px solid ${theme.colors.printBorder}; padding-bottom: 2px; }
          .firma-img { max-height: 40px; max-width: 100%; display: block; margin: 2px auto; }
          .firma-nombre { font-size: 8.5pt; text-align: center; margin-top: 2px; border-top: 1px solid ${theme.colors.printBorderLight}; padding-top: 2px; }
          .firma-linea { margin-top: 30px; border-top: 1px dashed ${theme.colors.printBorderDashed}; font-size: 8.5pt; text-align: center; padding-top: 2px; }

          .foto-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; border: 1px solid ${theme.colors.printBorder}; padding: 4px; min-height: 120px; margin-top: 4px; }
          .foto-thumb { width: 100%; aspect-ratio: 4/3; object-fit: cover; border: 1px solid ${theme.colors.printFotoThumbBorder}; }

          .page-break { page-break-before: always; }
        </style>
      </head>
      <body>
        <div class="wrap">
          <div class="doc-header">
            <div>
              <div class="doc-title">Check List de Equipos</div>
              ${checklist?.id ? `<div class="doc-subtitle">${esc(formatChlId(checklist.id))}</div>` : ''}
            </div>
            <div class="familia-header">${esc(familiaNombre)}</div>
            <div class="doc-header-right">
              <div class="tipo-box">
                <div>Ingreso: ${checklist?.tipo === 'ingreso' ? '&#9745;' : '&#9744;'}</div>
                <div>Egreso: ${checklist?.tipo === 'egreso' ? '&#9745;' : '&#9744;'}</div>
              </div>
              ${logoHtml}
            </div>
          </div>

          <div class="info-grid">
            <div><div class="info-label">Interno</div><div class="info-value">${esc(equipo?.interno || '')}</div></div>
            <div><div class="info-label">Marca - Modelo</div><div class="info-value">${esc(marcaModelo)}</div></div>
            <div><div class="info-label">Descripción</div><div class="info-value">${esc(equipo?.descripcion || checklist?.descripcion || '')}</div></div>
            <div><div class="info-label">Familia</div><div class="info-value">${esc(familiaNombre)}</div></div>
            <div><div class="info-label">Tipo de Equipo</div><div class="info-value">${esc(equipo?.tipo_nombre || equipo?.tipo || '')}</div></div>
            <div><div class="info-label">Horas / KM</div><div class="info-value">${blank ? '' : esc(checklist?.horas_km || '')}</div></div>
          </div>

          <div class="date-grid">
            <div><div class="info-label">Origen</div><div class="info-value">${blank ? '' : esc(checklist?.origen_nombre || '')}</div></div>
            <div><div class="info-label">Destino</div><div class="info-value">${blank ? '' : esc(checklist?.destino_nombre || '')}</div></div>
            <div><div class="info-label">Fecha</div><div class="info-value">${esc(fechaFormato)}</div></div>
            <div><div class="info-label">Tipo</div><div class="info-value" style="text-transform:capitalize">${esc(checklist?.tipo || '')}</div></div>
          </div>

          ${sistemasHtml}

          <div style="margin-top:4px">
            <div class="section-title">Último servicio</div>
            <div class="date-grid" style="grid-template-columns: 1fr 1fr 1fr 1fr">
              <div><div class="info-label">HS</div><div class="info-value">${blank ? '' : esc(checklist?.ultimo_servicio_hs || '')}</div></div>
              <div><div class="info-label">Tipo</div><div class="info-value">${blank ? '' : esc(checklist?.ultimo_servicio_tipo || '')}</div></div>
            </div>
          </div>

          <div class="section-title" style="margin-top:4px">Referencias</div>
          <table class="referencia-table">
            <tbody>
              <tr>
                <td><strong>B</strong> = Bueno</td>
                <td><strong>R</strong> = Regular</td>
                <td><strong>M</strong> = Malo</td>
                <td><strong>VO</strong> = Ver Observaciones</td>
                <td><strong>NC</strong> = No Controlado</td>
                <td><strong>P</strong> = Posee</td>
                <td><strong>NP</strong> = No Posee</td>
              </tr>
            </tbody>
          </table>

          <div style="margin-top:4px">
            <div class="section-title">Reparaciones pendientes</div>
            ${blank ? '<div class="lined-block"></div>' : `<div class="obs-block">${esc(checklist?.reparaciones_pendientes || '')}</div>`}
          </div>

          <div style="margin-top:4px">
            <div class="section-title">Observaciones</div>
            ${blank ? '<div class="lined-block" style="min-height:80px"></div>' : `<div class="obs-block" style="min-height:50px">${esc(checklist?.observaciones || '')}</div>`}
          </div>

          <div class="result-row">
            <div class="result-box result-ok">OPERATIVO ${resultOpActivo ? '&#9745;' : '&#9744;'}</div>
            <div class="result-box result-cond">OPERATIVO CONDICIONAL ${resultCondActivo ? '&#9745;' : '&#9744;'}</div>
          </div>
          <div class="nota-condicional">NOTA: SI EL RESULTADO ES OPERATIVO CONDICIONAL, PRECISA DE UN PLAN DE ACCION DE LOS DESVIOS DETECTADOS</div>

          <div class="sign-section">${firmasHtml}</div>

          ${fotosHtml}
        </div>
        <script>
          window.onload = function () {
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                window.print();
              });
            });
          };
        </script>
      </body>
    </html>
  `;
};

export default buildCheckListPrintHtml;
